import type { FieldPoint, FieldSpec, FieldZone } from './field';
import type { TrackPoint } from './types';
import { TOWER_FIELD } from './tower-map';
import { GRAVITY_BOWLS_FIELD } from './gravity-bowls-map';

// ---------------------------------------------------------------------------
// 신규 맵 6종 — "오늘툴 - 운명의 마블 신규 맵 6종" 디자인 도면의 좌표를 기반으로,
// 물리적으로 완주 가능하도록 보정한 스펙. 좌표계는 디자인 도면과 동일한 px.
// ---------------------------------------------------------------------------

const DEG = Math.PI / 180;

// ---------------------------------------------------------------------------
// 맵 1 · 컨베이어 팩토리 (1600×900, 탑뷰 · 좌→우)
// ---------------------------------------------------------------------------

export const CONVEYOR_FIELD: FieldSpec = {
  shapes: [
    { kind: 'room', x: 60, y: 270, w: 240, h: 360, r: 34 },
    { kind: 'pipe', points: [{ x: 268, y: 330 }, { x: 700, y: 330 }], width: 88 },
    { kind: 'pipe', points: [{ x: 268, y: 450 }, { x: 700, y: 450 }], width: 88 },
    { kind: 'pipe', points: [{ x: 268, y: 570 }, { x: 700, y: 570 }], width: 88 },
    { kind: 'room', x: 690, y: 270, w: 230, h: 360, r: 28 },
    { kind: 'pipe', points: [{ x: 890, y: 340 }, { x: 1016, y: 340 }], width: 110, endWidth: 56 },
    { kind: 'pipe', points: [{ x: 890, y: 450 }, { x: 1016, y: 450 }], width: 110, endWidth: 56 },
    { kind: 'pipe', points: [{ x: 890, y: 560 }, { x: 1016, y: 560 }], width: 110, endWidth: 56 },
    // A·B는 여러 번 접히는 장거리 생산 레인이다. C는 문이 열리면
    // 아래쪽 직선 벨트를 타고 짧게 합류하는 실제 급행 경로다.
    { kind: 'pipe', points: [
      { x: 1010, y: 340 }, { x: 1050, y: 205 }, { x: 1105, y: 340 },
      { x: 1160, y: 205 }, { x: 1215, y: 340 }, { x: 1270, y: 205 },
      { x: 1325, y: 340 }, { x: 1380, y: 405 },
    ], width: 70 },
    { kind: 'pipe', points: [
      { x: 1010, y: 450 }, { x: 1060, y: 540 }, { x: 1115, y: 410 },
      { x: 1170, y: 540 }, { x: 1225, y: 410 }, { x: 1280, y: 540 },
      { x: 1335, y: 440 }, { x: 1380, y: 438 },
    ], width: 70 },
    { kind: 'pipe', points: [
      { x: 1010, y: 560 }, { x: 1040, y: 640 }, { x: 1195, y: 640 },
      { x: 1290, y: 560 }, { x: 1380, y: 458 },
    ], width: 86 },
    { kind: 'room', x: 1360, y: 300, w: 100, h: 300, r: 28 },
    { kind: 'pipe', points: [{ x: 1420, y: 450 }, { x: 1534, y: 450 }], width: 300, endWidth: 64 },
    { kind: 'room', x: 1520, y: 390, w: 70, h: 120, r: 18 },
  ],
  holes: [],
  gravity: { x: 0, y: 0 },
  damping: 1.05,
  spawn: { kind: 'rect', x: 76, y: 286, w: 208, h: 328, pitch: 22 },
  startGate: { x: 294, y: 282, w: 12, h: 336 },
  finish: { kind: 'rect', x: 1530, y: 396, w: 56, h: 108 },
  obstacles: [
    { kind: 'paddle', id: 'cv-paddle-a', x: 700, y: 390, length: 120, thickness: 12, speed: 82 * DEG, phase: .42 },
    { kind: 'paddle', id: 'cv-paddle-b', x: 700, y: 510, length: 120, thickness: 12, speed: -113 * DEG, phase: 1.52 },
    { kind: 'gate', id: 'cv-pgate-1', x: 912, y: 288, w: 12, h: 104, schedule: { type: 'cycle', period: 2.4, openSeconds: 1.4, phase: 0 }, eventType: 'gate-close' },
    { kind: 'gate', id: 'cv-pgate-2', x: 912, y: 398, w: 12, h: 104, schedule: { type: 'cycle', period: 2.4, openSeconds: 1.4, phase: .8 }, eventType: 'gate-close' },
    { kind: 'gate', id: 'cv-pgate-3', x: 912, y: 508, w: 12, h: 104, schedule: { type: 'cycle', period: 2.4, openSeconds: 1.4, phase: 1.6 }, eventType: 'gate-close' },
    { kind: 'gate', id: 'cv-scgate', x: 1004, y: 506, w: 12, h: 112, schedule: { type: 'open-at', at: 24 }, eventType: 'shortcut-open' },
    { kind: 'paddle', id: 'cv-spin-a', x: 1400, y: 385, length: 76, thickness: 12, speed: 104 * DEG, phase: .31 },
    { kind: 'paddle', id: 'cv-spin-b', x: 1418, y: 515, length: 76, thickness: 12, speed: -137 * DEG, phase: 1.48 },
    // 기존 엇박 회전봉 뒤에서 양쪽 키커가 번갈아 결승 깔때기로 밀어낸다.
    { kind: 'kicker', id: 'cv-kick-a', x: 1480, y: 350, length: 48, thickness: 11, restAngle: 180 * DEG, hitAngle: 90 * DEG, hitSeconds: .17, returnSeconds: .95, cooldown: 1.7, triggerRadius: 98, triggerCount: 1, phase: .1 },
    { kind: 'kicker', id: 'cv-kick-b', x: 1480, y: 550, length: 48, thickness: 11, restAngle: -180 * DEG, hitAngle: -90 * DEG, hitSeconds: .17, returnSeconds: .95, cooldown: 1.7, triggerRadius: 98, triggerCount: 1, phase: .82 },
  ],
  zones: [
    { kind: 'belt', rect: { x: 300, y: 286, w: 400, h: 88 }, vx: 180, grip: 4.2, lane: 0 },
    { kind: 'belt', rect: { x: 300, y: 406, w: 400, h: 88 }, vx: 130, grip: 4.2, lane: 1 },
    { kind: 'belt', rect: { x: 300, y: 526, w: 400, h: 88 }, vx: 220, grip: 5.2, lane: 2 },
    { kind: 'accel', rect: { x: 60, y: 270, w: 240, h: 360 }, ax: 58, ay: 0 },
    { kind: 'accel', rect: { x: 690, y: 260, w: 470, h: 380 }, ax: 64, ay: 0 },
    { kind: 'accel', rect: { x: 1000, y: 175, w: 380, h: 220 }, ax: 22, ay: 0 },
    { kind: 'accel', rect: { x: 1000, y: 395, w: 380, h: 165 }, ax: 24, ay: 0 },
    { kind: 'accel', rect: { x: 996, y: 535, w: 82, h: 150 }, ax: 240, ay: 270 },
    { kind: 'belt', rect: { x: 1030, y: 598, w: 280, h: 94 }, vx: 430, grip: 8, lane: 2 },
    { kind: 'accel', rect: { x: 1190, y: 520, w: 210, h: 180 }, ax: 360, ay: -300 },
    { kind: 'accel', rect: { x: 1360, y: 290, w: 240, h: 320 }, ax: 92, ay: 0 },
  ],
  events: [
    { type: 'belt-reverse', duration: 6, window: [16, 21] },
    { type: 'gate-close', duration: 1.6, window: [10, 19] },
    { type: 'shortcut-open', duration: 2, window: [11, 13] },
  ],
  eventZones: [
    { type: 'belt-reverse', rect: { x: 300, y: 248, w: 400, h: 404 } },
    { type: 'gate-close', rect: { x: 900, y: 300, w: 130, h: 300 } },
    { type: 'shortcut-open', rect: { x: 980, y: 510, w: 440, h: 190 } },
  ],
  landmarks: [
    { x: 500, y: 236, label: '3레인 컨베이어' },
    { x: 805, y: 226, label: '재집결 광장' },
    { x: 964, y: 668, label: '압력 게이트' },
    { x: 1170, y: 724, label: 'C레인 급행' },
    { x: 1390, y: 266, label: '엇박 회전봉' },
  ],
  stallSeconds: 3.2,
  rushAt: 30,
};

// ---------------------------------------------------------------------------
// 맵 2 · 소용돌이 아레나 (1300×1300, 탑뷰 · 외곽→중앙 나선)
// ---------------------------------------------------------------------------

const VORTEX_CENTER = { x: 650, y: 650 } as const;
const VORTEX_TURNS = 3;
const VORTEX_OUTER_R = 540;
const VORTEX_INNER_R = 176;
const VORTEX_TAIL_FROM = .93;
// D1은 바깥 레인 위에만 걸쳐야 한다. 예전 (960, 330) 배치는 다음 나선과
// 원판을 이어 출발 셔플 중인 마블이 시작 게이트를 우회하는 지름길을 만들었다.
const VORTEX_DISK_1 = { x: 1032, y: 243, r: 68 } as const;

function vortexPoint(fraction: number): FieldPoint {
  const angle = -Math.PI / 2 + fraction * VORTEX_TURNS * Math.PI * 2;
  const radius = VORTEX_OUTER_R - (VORTEX_OUTER_R - VORTEX_INNER_R) * fraction;
  return {
    x: VORTEX_CENTER.x + Math.cos(angle) * radius,
    y: VORTEX_CENTER.y + Math.sin(angle) * radius,
  };
}

function vortexSpiralPoints(step: number, fromFraction: number, toFraction: number): FieldPoint[] {
  const count = Math.max(2, Math.ceil(VORTEX_TURNS * 360 * (toFraction - fromFraction) / step));
  return Array.from({ length: count + 1 }, (_, index) =>
    vortexPoint(fromFraction + (toFraction - fromFraction) * index / count));
}

export const VORTEX_FIELD: FieldSpec = {
  shapes: [
    { kind: 'pipe', points: vortexSpiralPoints(7, 0, VORTEX_TAIL_FROM), width: 120, endWidth: 96 },
    // 홀 진입 병목 — 나선 꼬리가 폭 46까지 좁아지며 홀로 흘러든다.
    { kind: 'pipe', points: vortexSpiralPoints(4, VORTEX_TAIL_FROM, 1), width: 96, endWidth: 56 },
    { kind: 'disc', ...VORTEX_DISK_1 },
    { kind: 'disc', x: 340, y: 970, r: 92 },
    { kind: 'disc', x: 650, y: 650, r: 190 },
  ],
  holes: [
    { kind: 'disc', x: VORTEX_DISK_1.x, y: VORTEX_DISK_1.y, r: 14 },
    { kind: 'disc', x: 340, y: 970, r: 14 },
  ],
  gravity: { x: 0, y: 0 },
  damping: .56,
  spawn: { kind: 'arc', cx: 650, cy: 650, r: 538, from: -90 * DEG, to: -4 * DEG, rows: 3, pitch: 21, drift: -19.3 },
  startGate: { x: 1124, y: 622, w: 116, h: 14 },
  finish: { kind: 'circle', x: 650, y: 650, r: 48 },
  obstacles: [
    { kind: 'disk', id: 'vx-disk-1', ...VORTEX_DISK_1, hubR: 14, speed: 38 * DEG, grip: 1.4, eventType: 'disk-reverse' },
    { kind: 'disk', id: 'vx-disk-2', x: 340, y: 970, r: 92, hubR: 14, speed: -38 * DEG, grip: 1.4, eventType: 'disk-reverse' },
    { kind: 'paddle', id: 'vx-spin-hall', x: 650, y: 502, length: 104, thickness: 12, speed: 100 * DEG, phase: .35 },
    { kind: 'shutter', id: 'vx-shutter', x: 650, y: 650, r: 66, openSeconds: 3, closeSeconds: 4, phase: 1.2, eventType: 'shutter-open' },
  ],
  zones: [
    { kind: 'radial', x: 650, y: 650, rMin: 430, rMax: 660, inward: 50, spin: 214, eventType: 'boost-ring', eventSpinScale: 2.3 },
    { kind: 'radial', x: 650, y: 650, rMin: 196, rMax: 430, inward: 50, spin: 214 },
    { kind: 'radial', x: 650, y: 650, rMin: 0, rMax: 196, inward: 78, spin: 36 },
    { kind: 'wait', circle: { x: 650, y: 650, r: 212 }, calm: 2.2 },
  ],
  events: [
    { type: 'disk-reverse', duration: 4, window: [9, 16] },
    { type: 'shutter-open', duration: 2.6, window: [12, 20] },
    { type: 'boost-ring', duration: 3, window: [8, 15] },
  ],
  eventZones: [
    { type: 'disk-reverse', circle: { x: VORTEX_DISK_1.x, y: VORTEX_DISK_1.y, r: 84 } },
    { type: 'shutter-open', circle: { x: 650, y: 650, r: 84 } },
    { type: 'boost-ring', circle: { x: 650, y: 650, r: 560 } },
  ],
  landmarks: [
    { x: 650, y: 84, label: '출발 — 외곽 링' },
    { x: VORTEX_DISK_1.x, y: 142, label: '회전 원판 D1' },
    { x: 340, y: 1092, label: '회전 원판 D2' },
    { x: 650, y: 872, label: '재집결 홀' },
  ],
  stallSeconds: 5.5,
  rushAt: 28,
};

export function vortexGuidePath(): TrackPoint[] {
  const points: TrackPoint[] = [];
  const steps = VORTEX_TURNS * 18;
  for (let index = 0; index <= steps; index += 1) {
    const point = vortexPoint(index / steps);
    points.push({ x: Math.round(point.x), y: Math.round(point.y), width: 96 });
  }
  points.push({ x: 650, y: 470, width: 60 });
  points.push({ x: 650, y: 566, width: 150 });
  points.push({ x: 650, y: 650, width: 90 });
  return points;
}

// ---------------------------------------------------------------------------
// 맵 3 · 시소 캐니언 (1200×1500, 중력 ↓ · 지그재그 하강)
// ---------------------------------------------------------------------------

export const SEESAW_FIELD: FieldSpec = {
  shapes: [
    { kind: 'room', x: 80, y: 60, w: 440, h: 200, r: 30 },
    { kind: 'pipe', points: [{ x: 400, y: 252 }, { x: 332, y: 338 }], width: 82, endWidth: 70 },
    { kind: 'room', x: 70, y: 330, w: 620, h: 200, r: 30 },
    { kind: 'pipe', points: [{ x: 646, y: 522 }, { x: 714, y: 648 }], width: 84, endWidth: 72 },
    { kind: 'room', x: 470, y: 640, w: 660, h: 200, r: 30 },
    { kind: 'pipe', points: [{ x: 736, y: 832 }, { x: 664, y: 958 }], width: 78, endWidth: 68 },
    { kind: 'room', x: 90, y: 950, w: 640, h: 200, r: 30 },
    { kind: 'pipe', points: [{ x: 420, y: 1142 }, { x: 488, y: 1268 }], width: 78, endWidth: 68 },
    { kind: 'room', x: 440, y: 1260, w: 580, h: 180, r: 30 },
    { kind: 'pipe', points: [{ x: 332, y: 250 }, { x: 332, y: 340 }], width: 60 },
  ],
  holes: [
    // 각 대기실 바닥은 다음 낙하구를 가장 낮은 점으로 삼는다. 완만한 경사라
    // 줄은 잠시 생겨도 정지하지 않고 실제 중력으로 출구 쪽으로 굴러간다.
    { kind: 'poly', points: [{ x: 78, y: 242 }, { x: 296, y: 260 }, { x: 296, y: 270 }, { x: 78, y: 270 }] },
    { kind: 'poly', points: [{ x: 446, y: 260 }, { x: 522, y: 244 }, { x: 522, y: 270 }, { x: 446, y: 270 }] },
    { kind: 'poly', points: [{ x: 68, y: 492 }, { x: 604, y: 530 }, { x: 604, y: 540 }, { x: 68, y: 540 }] },
    { kind: 'poly', points: [{ x: 692, y: 500 }, { x: 692, y: 540 }, { x: 684, y: 530 }] },
    { kind: 'poly', points: [{ x: 468, y: 796 }, { x: 696, y: 840 }, { x: 696, y: 850 }, { x: 468, y: 850 }] },
    { kind: 'poly', points: [{ x: 776, y: 840 }, { x: 1132, y: 790 }, { x: 1132, y: 850 }, { x: 776, y: 850 }] },
    { kind: 'poly', points: [{ x: 88, y: 1110 }, { x: 382, y: 1150 }, { x: 382, y: 1160 }, { x: 88, y: 1160 }] },
    { kind: 'poly', points: [{ x: 458, y: 1150 }, { x: 732, y: 1108 }, { x: 732, y: 1160 }, { x: 458, y: 1160 }] },
    { kind: 'poly', points: [{ x: 438, y: 1394 }, { x: 962, y: 1432 }, { x: 1022, y: 1398 }, { x: 1022, y: 1450 }, { x: 438, y: 1450 }] },
  ],
  gravity: { x: 0, y: 520 },
  damping: .14,
  spawn: { kind: 'rect', x: 96, y: 76, w: 408, h: 150, pitch: 22 },
  startGate: { x: 296, y: 234, w: 150, h: 14 },
  finish: { kind: 'rect', x: 946, y: 1268, w: 66, h: 164 },
  obstacles: [
    { kind: 'seesaw', id: 'ss-1', x: 300, y: 420, length: 320, thickness: 16, maxAngle: 14 * DEG, spring: 86, damping: 58, eventType: 'seesaw-free' },
    { kind: 'seesaw', id: 'ss-2', x: 800, y: 730, length: 320, thickness: 16, maxAngle: 14 * DEG, spring: 86, damping: 58, eventType: 'seesaw-free' },
    { kind: 'seesaw', id: 'ss-3', x: 400, y: 1040, length: 320, thickness: 16, maxAngle: 14 * DEG, spring: 86, damping: 58, eventType: 'seesaw-free' },
    { kind: 'seesaw', id: 'ss-4', x: 750, y: 1350, length: 280, thickness: 16, maxAngle: 12 * DEG, spring: 104, damping: 62, eventType: 'seesaw-free' },
    { kind: 'paddle', id: 'ss-spin-1', x: 604, y: 474, length: 86, thickness: 12, speed: 108 * DEG, phase: .3, reverseEvery: 2.6 },
    { kind: 'kicker', id: 'ss-kick-1', x: 684, y: 510, length: 54, thickness: 11, restAngle: 180 * DEG, hitAngle: 90 * DEG, hitSeconds: .17, returnSeconds: 1.1, cooldown: 1.65, triggerRadius: 104, triggerCount: 2, phase: .16 },
    { kind: 'kicker', id: 'ss-kick-2', x: 774, y: 816, length: 62, thickness: 12, restAngle: 180 * DEG, hitAngle: 90 * DEG, hitSeconds: .18, returnSeconds: 1.1, cooldown: 1.8, triggerRadius: 102, triggerCount: 2, phase: .42 },
    { kind: 'bumper', id: 'ss-bumper-1', x: 600, y: 1030, r: 24 },
    { kind: 'bumper', id: 'ss-bumper-2', x: 560, y: 1338, r: 24 },
    { kind: 'gate', id: 'ss-wallgate', x: 302, y: 282, w: 60, h: 12, schedule: { type: 'open-at', at: Number.POSITIVE_INFINITY }, eventType: 'wall-open' },
  ],
  zones: [
    { kind: 'accel', rect: { x: 80, y: 60, w: 440, h: 200 }, ax: 26, ay: 0 },
    { kind: 'accel', rect: { x: 70, y: 330, w: 620, h: 200 }, ax: 118, ay: 0 },
    // 기본 흐름은 시소 끝으로 분산해 떨어뜨리고, 반전 이벤트 때만 중앙으로 몰아 혼잡을 만든다.
    { kind: 'accel', rect: { x: 470, y: 640, w: 320, h: 200 }, ax: -60, ay: 0, eventType: 'tilt-reverse' },
    { kind: 'accel', rect: { x: 790, y: 640, w: 340, h: 200 }, ax: 60, ay: 0, eventType: 'tilt-reverse' },
    { kind: 'accel', rect: { x: 470, y: 770, w: 266, h: 70 }, ax: 180, ay: 0 },
    { kind: 'accel', rect: { x: 736, y: 770, w: 394, h: 70 }, ax: -180, ay: 0 },
    { kind: 'accel', rect: { x: 90, y: 950, w: 330, h: 200 }, ax: 30, ay: 0 },
    { kind: 'accel', rect: { x: 470, y: 950, w: 260, h: 200 }, ax: -38, ay: 0 },
    { kind: 'guide', rect: { x: 350, y: 1080, w: 190, h: 230 }, accel: 320 },
    { kind: 'accel', rect: { x: 440, y: 1260, w: 580, h: 180 }, ax: 40, ay: 0 },
  ],
  events: [
    { type: 'seesaw-free', duration: 4, window: [9, 15] },
    { type: 'tilt-reverse', duration: 5, window: [13, 19] },
    { type: 'wall-open', duration: 2, window: [8, 14] },
  ],
  eventZones: [
    { type: 'seesaw-free', rect: { x: 70, y: 330, w: 620, h: 200 } },
    { type: 'tilt-reverse', rect: { x: 470, y: 640, w: 660, h: 200 } },
    { type: 'wall-open', rect: { x: 286, y: 240, w: 108, h: 124 } },
  ],
  landmarks: [
    { x: 300, y: 314, label: '시소 SS1' },
    { x: 800, y: 624, label: '시소 SS2' },
    { x: 400, y: 934, label: '시소 SS3' },
    { x: 744, y: 1244, label: '결승 시소 SS4' },
  ],
  stallSeconds: 3.4,
  rushAt: 24,
};

// ---------------------------------------------------------------------------
// 맵 4 · 중력 환승역 (1500×1100, 방마다 다른 중력 벡터 · 방 C는 역중력 상승)
// ---------------------------------------------------------------------------

export const GRAVITY_HUB_FIELD: FieldSpec = {
  shapes: [
    { kind: 'room', x: 80, y: 60, w: 420, h: 300, r: 30 },
    // 첫 환승구는 위쪽이 살짝 넓은 V자 깔때기다. 회전봉이 세로로 서도
    // 좌우 중 한쪽에는 마블 한 개가 빠질 실제 충돌 여유가 남는다.
    { kind: 'pipe', points: [{ x: 290, y: 348 }, { x: 290, y: 448 }], width: 96, endWidth: 72 },
    { kind: 'room', x: 80, y: 440, w: 560, h: 260, r: 30 },
    { kind: 'pipe', points: [{ x: 632, y: 570 }, { x: 748, y: 570 }], width: 72 },
    { kind: 'room', x: 740, y: 440, w: 300, h: 520, r: 30 },
    { kind: 'pipe', points: [{ x: 890, y: 448 }, { x: 890, y: 380 }, { x: 1108, y: 380 }], width: 72 },
    { kind: 'room', x: 1100, y: 240, w: 340, h: 340, r: 30 },
    { kind: 'pipe', points: [{ x: 1270, y: 572 }, { x: 1270, y: 680 }, { x: 1448, y: 680 }], width: 72 },
    { kind: 'room', x: 1400, y: 620, w: 80, h: 120, r: 18 },
  ],
  holes: [
    // 방마다 실제 중력 방향의 환승구가 가장 낮은(또는 가장 먼) 점이 되도록
    // 바닥·측벽을 얕은 V자로 깎아 평평한 사각 대기실을 없앤다.
    { kind: 'poly', points: [{ x: 78, y: 326 }, { x: 238, y: 360 }, { x: 238, y: 372 }, { x: 78, y: 372 }] },
    { kind: 'poly', points: [{ x: 342, y: 360 }, { x: 502, y: 326 }, { x: 502, y: 372 }, { x: 342, y: 372 }] },
    { kind: 'poly', points: [{ x: 602, y: 438 }, { x: 642, y: 438 }, { x: 642, y: 530 }, { x: 612, y: 530 }] },
    { kind: 'poly', points: [{ x: 612, y: 610 }, { x: 642, y: 610 }, { x: 642, y: 702 }, { x: 602, y: 702 }] },
    { kind: 'poly', points: [{ x: 738, y: 438 }, { x: 850, y: 438 }, { x: 738, y: 474 }] },
    { kind: 'poly', points: [{ x: 930, y: 438 }, { x: 1042, y: 438 }, { x: 1042, y: 474 }] },
    { kind: 'poly', points: [{ x: 1098, y: 542 }, { x: 1230, y: 580 }, { x: 1230, y: 592 }, { x: 1098, y: 592 }] },
    { kind: 'poly', points: [{ x: 1310, y: 580 }, { x: 1442, y: 542 }, { x: 1442, y: 592 }, { x: 1310, y: 592 }] },
  ],
  gravity: { x: 0, y: 470 },
  damping: .12,
  spawn: { kind: 'rect', x: 96, y: 74, w: 392, h: 180, pitch: 21 },
  startGate: { x: 242, y: 348, w: 96, h: 12 },
  finish: { kind: 'rect', x: 1428, y: 626, w: 50, h: 108 },
  obstacles: [
    { kind: 'paddle', id: 'gh-spin-a', x: 290, y: 330, length: 140, thickness: 14, speed: 116 * DEG, phase: .42, reverseEvery: 3.2 },
    { kind: 'paddle', id: 'gh-spin-c', x: 880, y: 520, length: 140, thickness: 14, speed: -100 * DEG, phase: -.52 },
    { kind: 'paddle', id: 'gh-spin-d', x: 1270, y: 420, length: 140, thickness: 14, speed: 100 * DEG, phase: .2 },
    { kind: 'kicker', id: 'gh-kick-b', x: 580, y: 642, length: 52, thickness: 11, restAngle: -90 * DEG, hitAngle: -18 * DEG, hitSeconds: .18, returnSeconds: .82, cooldown: 1.45, triggerRadius: 108, triggerCount: 1, phase: .18, eventType: 'transfer-delay' },
    { kind: 'kicker', id: 'gh-kick-c', x: 810, y: 476, length: 58, thickness: 11, restAngle: 0, hitAngle: -68 * DEG, hitSeconds: .18, returnSeconds: .86, cooldown: 1.5, triggerRadius: 112, triggerCount: 1, phase: .62, eventType: 'room-reverse' },
    { kind: 'kicker', id: 'gh-kick-d', x: 1310, y: 556, length: 58, thickness: 11, restAngle: 180 * DEG, hitAngle: 90 * DEG, hitSeconds: .18, returnSeconds: .82, cooldown: 1.45, triggerRadius: 108, triggerCount: 1, phase: 1.02, eventType: 'transfer-delay' },
    { kind: 'bumper', id: 'gh-bumper-b1', x: 300, y: 620, r: 26 },
    { kind: 'bumper', id: 'gh-bumper-b2', x: 460, y: 530, r: 26 },
    { kind: 'bumper', id: 'gh-bumper-d', x: 1180, y: 512, r: 26 },
    { kind: 'gate', id: 'gh-tgate-1', x: 254, y: 392, w: 72, h: 14, schedule: { type: 'crowd', count: 12, waitSeconds: 1.4, holdSeconds: 1.2, radius: 150 }, eventType: 'transfer-delay' },
    { kind: 'gate', id: 'gh-tgate-2', x: 668, y: 534, w: 14, h: 72, schedule: { type: 'crowd', count: 12, waitSeconds: 1.4, holdSeconds: 1.2, radius: 150 }, eventType: 'transfer-delay' },
    { kind: 'gate', id: 'gh-tgate-3', x: 854, y: 424, w: 72, h: 14, schedule: { type: 'crowd', count: 10, waitSeconds: 1.4, holdSeconds: 1.2, radius: 150 }, eventType: 'transfer-delay' },
    { kind: 'gate', id: 'gh-tgate-4', x: 1234, y: 598, w: 72, h: 14, schedule: { type: 'crowd', count: 10, waitSeconds: 1.4, holdSeconds: 1.2, radius: 160 }, eventType: 'transfer-delay' },
  ],
  zones: [
    { kind: 'gravity', rect: { x: 632, y: 534, w: 116, h: 72 }, gx: 540, gy: 0 },
    { kind: 'gravity', rect: { x: 854, y: 344, w: 256, h: 74 }, gx: 400, gy: -360 },
    { kind: 'gravity', rect: { x: 1234, y: 572, w: 76, h: 82 }, gx: 0, gy: 610 },
    { kind: 'gravity', rect: { x: 1270, y: 644, w: 178, h: 74 }, gx: 470, gy: 190 },
    { kind: 'gravity', rect: { x: 1400, y: 620, w: 96, h: 122 }, gx: 220, gy: 420 },
    { kind: 'gravity', rect: { x: 80, y: 60, w: 420, h: 380 }, gx: 0, gy: 600, label: '방 A · g↓ ×1.3' },
    { kind: 'gravity', rect: { x: 80, y: 440, w: 560, h: 262 }, gx: 540, gy: 0, eventType: 'gravity-early', eventGx: 0, eventGy: 560, label: '방 B · g→ ×1.15' },
    { kind: 'gravity', rect: { x: 740, y: 380, w: 302, h: 582 }, gx: 0, gy: -540, eventType: 'room-reverse', eventGx: 0, eventGy: 560, label: '방 C · g↑ ×1.15' },
    { kind: 'gravity', rect: { x: 1100, y: 240, w: 342, h: 342 }, gx: 0, gy: 610, label: '방 D · g↓ ×1.3' },
    { kind: 'accel', rect: { x: 1310, y: 470, w: 130, h: 112 }, ax: -34, ay: 0 },
    { kind: 'accel', rect: { x: 1100, y: 470, w: 100, h: 112 }, ax: 34, ay: 0 },
    // 회전봉 전체가 아니라 게이트 바로 위만 의도적인 대기 구역으로 둔다.
    // 봉 끝에 끼인 마블은 stall 보정을 받아 다시 흐름에 합류한다.
    { kind: 'wait', rect: { x: 232, y: 350, w: 116, h: 66 } },
    { kind: 'wait', rect: { x: 556, y: 506, w: 116, h: 128 } },
    { kind: 'wait', rect: { x: 824, y: 432, w: 132, h: 120 } },
    { kind: 'wait', rect: { x: 1204, y: 496, w: 132, h: 106 } },
  ],
  events: [
    { type: 'gravity-early', duration: 3, window: [9, 15] },
    { type: 'room-reverse', duration: 4, window: [13, 19] },
    { type: 'transfer-delay', duration: 6, window: [10, 17] },
  ],
  eventZones: [
    { type: 'gravity-early', rect: { x: 80, y: 440, w: 560, h: 260 } },
    { type: 'room-reverse', rect: { x: 740, y: 440, w: 300, h: 520 } },
    { type: 'transfer-delay', rect: { x: 1100, y: 240, w: 340, h: 340 } },
  ],
  landmarks: [
    { x: 290, y: 34, label: '방 A · 출발장' },
    { x: 360, y: 726, label: '방 B · 재집결' },
    { x: 890, y: 988, label: '방 C · 역중력' },
    { x: 1270, y: 214, label: '방 D · 결승' },
  ],
  stallSeconds: 4.5,
  rushAt: 20,
};

// ---------------------------------------------------------------------------
// 맵 5 · 핀볼 리액터 (900×1600, 중력 ×1.3 · 상→하 고속)
// ---------------------------------------------------------------------------

function pinRow(idPrefix: string, y: number, xs: readonly number[], sink = false) {
  return xs.map((x, index) => ({ kind: 'pin', id: `${idPrefix}-${index}`, x, y, r: 9, sink } as const));
}

export const PINBALL_FIELD: FieldSpec = {
  shapes: [
    { kind: 'room', x: 200, y: 60, w: 500, h: 180, r: 26 },
    { kind: 'poly', points: [{ x: 212, y: 212 }, { x: 688, y: 212 }, { x: 556, y: 372 }, { x: 344, y: 372 }] },
    { kind: 'room', x: 70, y: 350, w: 760, h: 560, r: 34 },
    { kind: 'room', x: 140, y: 900, w: 150, h: 220, r: 20 },
    { kind: 'room', x: 376, y: 900, w: 150, h: 220, r: 20 },
    { kind: 'room', x: 612, y: 900, w: 150, h: 220, r: 20 },
    { kind: 'pipe', points: [{ x: 451, y: 1110 }, { x: 451, y: 1262 }], width: 80 },
    { kind: 'pipe', points: [{ x: 215, y: 1112 }, { x: 248, y: 1216 }, { x: 330, y: 1276 }, { x: 450, y: 1298 }, { x: 560, y: 1276 }, { x: 660, y: 1212 }, { x: 700, y: 1112 }], width: 98 },
    { kind: 'disc', x: 450, y: 1400, r: 120 },
    { kind: 'room', x: 392, y: 1500, w: 116, h: 80, r: 16 },
  ],
  holes: [
    { kind: 'disc', x: 450, y: 1400, r: 11 },
    // 핀 아레나 바닥은 세 드롭 게이트가 낮은 골이 되도록, 드롭실은
    // 각 합류 파이프가 낮은 점이 되도록 얕게 기울인다.
    { kind: 'poly', points: [{ x: 68, y: 894 }, { x: 138, y: 910 }, { x: 138, y: 922 }, { x: 68, y: 922 }] },
    { kind: 'poly', points: [{ x: 292, y: 910 }, { x: 334, y: 894 }, { x: 374, y: 910 }, { x: 374, y: 922 }, { x: 292, y: 922 }] },
    { kind: 'poly', points: [{ x: 528, y: 910 }, { x: 570, y: 894 }, { x: 610, y: 910 }, { x: 610, y: 922 }, { x: 528, y: 922 }] },
    { kind: 'poly', points: [{ x: 764, y: 910 }, { x: 832, y: 894 }, { x: 832, y: 922 }, { x: 764, y: 922 }] },
    { kind: 'poly', points: [{ x: 138, y: 1090 }, { x: 174, y: 1120 }, { x: 174, y: 1132 }, { x: 138, y: 1132 }] },
    { kind: 'poly', points: [{ x: 256, y: 1120 }, { x: 292, y: 1090 }, { x: 292, y: 1132 }, { x: 256, y: 1132 }] },
    { kind: 'poly', points: [{ x: 374, y: 1090 }, { x: 410, y: 1120 }, { x: 410, y: 1132 }, { x: 374, y: 1132 }] },
    { kind: 'poly', points: [{ x: 492, y: 1120 }, { x: 528, y: 1090 }, { x: 528, y: 1132 }, { x: 492, y: 1132 }] },
    { kind: 'poly', points: [{ x: 610, y: 1090 }, { x: 658, y: 1120 }, { x: 658, y: 1132 }, { x: 610, y: 1132 }] },
    { kind: 'poly', points: [{ x: 742, y: 1120 }, { x: 764, y: 1090 }, { x: 764, y: 1132 }, { x: 742, y: 1132 }] },
  ],
  gravity: { x: 0, y: 430 },
  damping: .34,
  spawn: { kind: 'rect', x: 216, y: 76, w: 468, h: 148, pitch: 22 },
  startGate: { x: 330, y: 352, w: 240, h: 12 },
  finish: { kind: 'rect', x: 398, y: 1514, w: 104, h: 60 },
  obstacles: [
    ...pinRow('pb-pin-r1', 420, [130, 210, 290, 370, 450, 530, 610, 690, 770]),
    ...pinRow('pb-pin-r2', 500, [170, 250, 330, 410, 490, 570, 650, 730]),
    ...pinRow('pb-pin-r3', 580, [130, 210, 290, 370, 450, 530, 610, 690, 770]),
    ...pinRow('pb-pin-r4', 660, [170, 250, 330, 570, 650, 730], true),
    { kind: 'bumper', id: 'pb-bumper-1', x: 180, y: 760, r: 34 },
    { kind: 'bumper', id: 'pb-bumper-2', x: 450, y: 790, r: 34 },
    { kind: 'bumper', id: 'pb-bumper-3', x: 720, y: 760, r: 34 },
    { kind: 'flipper', id: 'pb-flip-l', x: 250, y: 862, length: 150, thickness: 15, restAngle: -34 * DEG, hitAngle: 22 * DEG, period: 1.8, swingSeconds: .22, phase: 0, eventType: 'flipper-frenzy' },
    { kind: 'flipper', id: 'pb-flip-r', x: 650, y: 862, length: 150, thickness: 15, restAngle: 34 * DEG, hitAngle: -22 * DEG, period: 1.8, swingSeconds: .22, phase: .9, eventType: 'flipper-frenzy' },
    { kind: 'gate', id: 'pb-dgate-1', x: 148, y: 884, w: 134, h: 14, schedule: { type: 'event-close' }, eventType: 'drop-close' },
    { kind: 'gate', id: 'pb-dgate-2', x: 384, y: 884, w: 134, h: 14, schedule: { type: 'event-close' }, eventType: 'drop-close' },
    { kind: 'gate', id: 'pb-dgate-3', x: 620, y: 884, w: 134, h: 14, schedule: { type: 'event-close' }, eventType: 'drop-close' },
    { kind: 'rotor', id: 'pb-rotor', x: 450, y: 1400, wingLength: 180, thickness: 15, speed: 80 * DEG, hubR: 11 },
  ],
  zones: [
    { kind: 'wait', circle: { x: 450, y: 1400, r: 126 } },
  ],
  events: [
    { type: 'flipper-frenzy', duration: 3, window: [9, 16] },
    { type: 'drop-close', duration: 2.6, window: [12, 18] },
    { type: 'pin-sink', duration: 2.5, window: [8, 14] },
  ],
  eventZones: [
    { type: 'flipper-frenzy', rect: { x: 70, y: 640, w: 760, h: 270 } },
    { type: 'drop-close', rect: { x: 140, y: 880, w: 622, h: 250 } },
    { type: 'pin-sink', rect: { x: 90, y: 620, w: 720, h: 90 } },
  ],
  landmarks: [
    { x: 450, y: 214, label: '진입 깔때기' },
    { x: 450, y: 636, label: '핀 아레나' },
    { x: 450, y: 1188, label: '합류 광장' },
    { x: 450, y: 1258, label: '회전 관문' },
  ],
  stallSeconds: 3.2,
  rushAt: 20,
};

// ---------------------------------------------------------------------------
// 맵 6 · 리프트 터미널 (1100×1500, 하→상 상승 · 해치 리프트 5기)
// ---------------------------------------------------------------------------

export const LIFT_FIELD: FieldSpec = {
  shapes: [
    { kind: 'room', x: 150, y: 1210, w: 640, h: 230, r: 30 },
    { kind: 'room', x: 222, y: 1032, w: 132, h: 408, r: 16 },
    { kind: 'room', x: 548, y: 1048, w: 168, h: 392, r: 16 },
    { kind: 'room', x: 120, y: 880, w: 300, h: 200, r: 26 },
    { kind: 'room', x: 560, y: 920, w: 420, h: 200, r: 26 },
    { kind: 'room', x: 360, y: 724, w: 104, h: 356, r: 16 },
    { kind: 'room', x: 776, y: 724, w: 128, h: 388, r: 16 },
    { kind: 'room', x: 180, y: 520, w: 830, h: 220, r: 30 },
    { kind: 'room', x: 904, y: 424, w: 98, h: 308, r: 16 },
    { kind: 'room', x: 800, y: 240, w: 220, h: 200, r: 26 },
  ],
  holes: [
    // 넓은 대기실 바닥을 각 리프트 승차점 쪽으로 낮춘다. 플랫폼 자체는
    // 기존 높이를 유지하고, 바닥에서 자연스럽게 굴러와 올라타게 한다.
    { kind: 'poly', points: [{ x: 148, y: 1402 }, { x: 224, y: 1440 }, { x: 224, y: 1452 }, { x: 148, y: 1452 }] },
    { kind: 'poly', points: [{ x: 352, y: 1440 }, { x: 450, y: 1408 }, { x: 556, y: 1440 }, { x: 556, y: 1452 }, { x: 352, y: 1452 }] },
    { kind: 'poly', points: [{ x: 704, y: 1440 }, { x: 792, y: 1402 }, { x: 792, y: 1452 }, { x: 704, y: 1452 }] },
    { kind: 'poly', points: [{ x: 118, y: 1038 }, { x: 210, y: 1080 }, { x: 210, y: 1090 }, { x: 118, y: 1090 }] },
    { kind: 'poly', points: [{ x: 900, y: 1120 }, { x: 982, y: 1084 }, { x: 982, y: 1130 }, { x: 900, y: 1130 }] },
    { kind: 'poly', points: [{ x: 178, y: 694 }, { x: 340, y: 740 }, { x: 340, y: 752 }, { x: 178, y: 752 }] },
    // 상단 리프트 하차 지점의 수직 턱을 마블 반경보다 낮게 줄인 완만한 경사.
    { kind: 'poly', points: [{ x: 480, y: 728 }, { x: 760, y: 740 }, { x: 760, y: 752 }, { x: 480, y: 752 }] },
    { kind: 'room', x: 208, y: 1236, w: 14, h: 126, r: 6 },
    // 중앙 출발 팩 양옆 가이드는 아래를 넓게 열어 셔플 뒤 양쪽 리프트로 흩어지게 한다.
    { kind: 'room', x: 354, y: 1254, w: 14, h: 82, r: 6 },
    { kind: 'room', x: 534, y: 1254, w: 14, h: 82, r: 6 },
    { kind: 'room', x: 716, y: 1236, w: 14, h: 126, r: 6 },
    { kind: 'room', x: 346, y: 876, w: 14, h: 114, r: 6 },
    { kind: 'room', x: 762, y: 912, w: 14, h: 112, r: 6 },
    { kind: 'room', x: 890, y: 516, w: 14, h: 124, r: 6 },
    { kind: 'room', x: 826, y: 514, w: 14, h: 144, r: 6 },
  ],
  gravity: { x: 0, y: 500 },
  damping: .13,
  spawn: { kind: 'rect', x: 166, y: 1228, w: 608, h: 156, pitch: 20, fill: 'center' },
  startGate: { x: 160, y: 1390, w: 620, h: 12 },
  finish: { kind: 'rect', x: 808, y: 248, w: 204, h: 176 },
  obstacles: [
    {
      kind: 'lift', id: 'lt-lift-1', x: 288, boardSurfaceY: 1430, crestSurfaceY: 1044, width: 116, thickness: 14,
      dumpAngle: -24 * DEG, period: 2.8, boardFraction: .28, riseFraction: .34, dumpFraction: .2, phase: 0,
      mouths: [{ x: 214, y: 1362, w: 14, h: 70 }, { x: 348, y: 1362, w: 14, h: 70 }],
      hatch: { x: 216, y: 1072, w: 148, h: 12 }, eventType: 'lift-express',
    },
    {
      kind: 'lift', id: 'lt-lift-2', x: 630, boardSurfaceY: 1430, crestSurfaceY: 1084, width: 144, thickness: 16,
      dumpAngle: 22 * DEG, period: 4.8, boardFraction: .24, riseFraction: .34, dumpFraction: .2, phase: .97,
      mouths: [{ x: 542, y: 1362, w: 14, h: 70 }, { x: 704, y: 1362, w: 14, h: 70 }],
      hatch: { x: 548, y: 1112, w: 172, h: 12 }, eventType: 'early-dump',
    },
    {
      kind: 'lift', id: 'lt-lift-3', x: 412, boardSurfaceY: 1068, crestSurfaceY: 702, width: 88, thickness: 14,
      dumpAngle: 24 * DEG, period: 2.8, boardFraction: .28, riseFraction: .34, dumpFraction: .2, phase: .5,
      mouths: [{ x: 346, y: 990, w: 14, h: 78 }],
      hatch: { x: 358, y: 732, w: 108, h: 12 }, eventType: 'lift-express',
    },
    {
      kind: 'lift', id: 'lt-lift-4', x: 840, boardSurfaceY: 1110, crestSurfaceY: 702, width: 112, thickness: 16,
      dumpAngle: -22 * DEG, period: 4.8, boardFraction: .24, riseFraction: .34, dumpFraction: .2, phase: .78,
      mouths: [{ x: 762, y: 1024, w: 14, h: 88 }],
      hatch: { x: 774, y: 732, w: 132, h: 12 }, eventType: 'early-dump',
    },
    {
      kind: 'lift', id: 'lt-lift-5', x: 953, boardSurfaceY: 730, crestSurfaceY: 400, width: 86, thickness: 14,
      dumpAngle: -26 * DEG, period: 2.8, boardFraction: .28, riseFraction: .34, dumpFraction: .2, phase: .25,
      mouths: [{ x: 890, y: 640, w: 14, h: 92 }],
      hatch: { x: 902, y: 432, w: 100, h: 12 }, eventType: 'lift-express',
    },
    { kind: 'paddle', id: 'lt-spin-1', x: 420, y: 648, length: 160, thickness: 14, speed: 110 * DEG, phase: .3, eventType: 'rotor-stop' },
    { kind: 'paddle', id: 'lt-spin-2', x: 700, y: 648, length: 160, thickness: 14, speed: -110 * DEG, phase: -.5, eventType: 'rotor-stop' },
    { kind: 'kicker', id: 'lt-kick-small', x: 300, y: 992, length: 58, thickness: 11, restAngle: 90 * DEG, hitAngle: 18 * DEG, hitSeconds: .24, returnSeconds: 1.12, cooldown: 1.75, triggerRadius: 106, triggerCount: 2, phase: .34, linkedLiftId: 'lt-lift-3', eventType: 'lift-express' },
    { kind: 'kicker', id: 'lt-kick-large', x: 680, y: 1028, length: 62, thickness: 11, restAngle: 90 * DEG, hitAngle: 18 * DEG, hitSeconds: .24, returnSeconds: 1.12, cooldown: 1.85, triggerRadius: 110, triggerCount: 2, phase: .88, linkedLiftId: 'lt-lift-4', eventType: 'early-dump' },
    { kind: 'bumper', id: 'lt-bumper-1', x: 300, y: 664, r: 26 },
    { kind: 'bumper', id: 'lt-bumper-2', x: 560, y: 664, r: 26 },
  ],
  zones: [
    { kind: 'accel', rect: { x: 158, y: 1210, w: 64, h: 226 }, ax: 85, ay: 0 },
    { kind: 'accel', rect: { x: 346, y: 1210, w: 104, h: 226 }, ax: -85, ay: 0 },
    { kind: 'accel', rect: { x: 450, y: 1210, w: 108, h: 226 }, ax: 85, ay: 0 },
    { kind: 'accel', rect: { x: 706, y: 1210, w: 84, h: 226 }, ax: -85, ay: 0 },
    { kind: 'accel', rect: { x: 120, y: 880, w: 300, h: 200 }, ax: 70, ay: 0 },
    // 소형 리프트 배출 방의 좌측 막다른 바닥과 대기 존 입구에만 복귀 흐름을 더한다.
    // 중앙 대기 존의 핵심은 기존 70px/s²를 유지해 리프트 병목과 충돌은 남겨 둔다.
    { kind: 'accel', rect: { x: 120, y: 880, w: 300, h: 200 }, ax: 440, ay: 0 },
    { kind: 'belt', rect: { x: 120, y: 880, w: 300, h: 200 }, vx: 150, grip: 3.2, lane: 2 },
    { kind: 'accel', rect: { x: 560, y: 920, w: 216, h: 200 }, ax: 170, ay: 0 },
    { kind: 'accel', rect: { x: 790, y: 920, w: 190, h: 200 }, ax: -170, ay: 0 },
    { kind: 'accel', rect: { x: 180, y: 520, w: 830, h: 212 }, ax: 180, ay: 0 },
    { kind: 'accel', rect: { x: 800, y: 240, w: 220, h: 200 }, ax: -40, ay: 0 },
    { kind: 'wait', rect: { x: 188, y: 1350, w: 262, h: 90 } },
    { kind: 'wait', rect: { x: 450, y: 1350, w: 304, h: 90 } },
    { kind: 'wait', rect: { x: 270, y: 996, w: 100, h: 80 } },
    { kind: 'wait', rect: { x: 726, y: 1020, w: 100, h: 92 } },
    { kind: 'wait', rect: { x: 838, y: 646, w: 166, h: 88 } },
  ],
  events: [
    { type: 'lift-express', duration: 4, window: [9, 16] },
    { type: 'early-dump', duration: 2, window: [12, 18] },
    { type: 'rotor-stop', duration: 2, window: [10, 16] },
  ],
  eventZones: [
    { type: 'lift-express', rect: { x: 120, y: 880, w: 300, h: 200 } },
    { type: 'early-dump', rect: { x: 560, y: 920, w: 420, h: 200 } },
    { type: 'rotor-stop', rect: { x: 180, y: 520, w: 830, h: 220 } },
  ],
  landmarks: [
    { x: 470, y: 1214, label: '출발장' },
    { x: 268, y: 854, label: '소형 리프트 경로' },
    { x: 770, y: 894, label: '대형 리프트 경로' },
    { x: 500, y: 494, label: '가로 통로 광장' },
    { x: 910, y: 214, label: '결승 터미널' },
  ],
  stallSeconds: 7,
  rushAt: 22,
};

// ---------------------------------------------------------------------------
// 맵 7 · 다중 벨트 야드 (v3 리메이크 R2 — 컨베이어 계승, 1756×1222, 탑뷰)
// "오늘툴 - 운명의 마블 기존맵 v3 리메이크" R2 도면(3160×2200u, 마블 r18)을
// ×10/18로 임포트했다. 시간·주기·각속도는 그대로, 길이·속도·가속도만 스케일.
// ---------------------------------------------------------------------------

/** v3 도면 좌표(u) → 엔진 px. 마블 반지름 18u → 10px. */
const V3 = 10 / 18;

export const BELT_YARD_FIELD: FieldSpec = {
  eventScheduling: 'occupancy',
  shapes: [
    // 출발장 540×680u (모서리 r250)
    { kind: 'room', x: 38.9, y: 77.8, w: 300, h: 377.8, r: 138.9 },
    // 벨트 뱅크 1 — 3속 병렬 (좌→우), 바닥 폭 200u
    { kind: 'pipe', points: [{ x: 333.3, y: 133.3 }, { x: 1405.6, y: 133.3 }], width: 111.1 },
    { kind: 'pipe', points: [{ x: 333.3, y: 266.7 }, { x: 1355.6, y: 266.7 }], width: 111.1 },
    { kind: 'pipe', points: [{ x: 333.3, y: 400 }, { x: 1333.3, y: 400 }], width: 111.1 },
    // 제한된 환승 틈: 충돌로 레인을 바꿀 수 있지만 자동 대각 이동은 없다.
    { kind: 'room', x: 688.9, y: 127, w: 77.8, h: 146, r: 24 },
    { kind: 'room', x: 994.4, y: 260, w: 77.8, h: 146, r: 24 },
    // 혼합 풀 600×1580u 타원
    { kind: 'ellipse', x: 1455.6, y: 516.7, rx: 166.7, ry: 438.9 },
    // 벨트 뱅크 2 — 역방향 (우→좌), 바닥 폭 210u
    { kind: 'pipe', points: [{ x: 1333.3, y: 655.6 }, { x: 311.1, y: 655.6 }], width: 116.7 },
    { kind: 'pipe', points: [{ x: 1361.1, y: 788.9 }, { x: 333.3, y: 788.9 }], width: 116.7 },
    { kind: 'pipe', points: [{ x: 1422.2, y: 922.2 }, { x: 311.1, y: 922.2 }], width: 116.7 },
    // 뱅크 2 레인 갭
    { kind: 'room', x: 822.2, y: 649, w: 77.8, h: 146, r: 24 },
    { kind: 'room', x: 1083.3, y: 782, w: 77.8, h: 146, r: 24 },
    // 전환 야드 560×660u 타원
    { kind: 'ellipse', x: 233.3, y: 788.9, rx: 155.6, ry: 183.3 },
    // 야드 → 벨트 C 연결 곡선
    {
      kind: 'pipe',
      points: [
        { x: 233.3, y: 966.7 }, { x: 237.8, y: 1034.4 }, { x: 258.3, y: 1082.2 },
        { x: 288.9, y: 1100 }, { x: 316.7, y: 1105.6 },
      ],
      width: 122.2,
    },
    // 벨트 C — 단일 광폭 300u
    { kind: 'pipe', points: [{ x: 311.1, y: 1105.6 }, { x: 1255.6, y: 1105.6 }], width: 166.7 },
    // 결승 전 챔버 460×380u 타원
    { kind: 'ellipse', x: 1355.6, y: 1105.6, rx: 127.8, ry: 105.6 },
    // 결승 깔때기
    { kind: 'poly', points: [{ x: 1472.2, y: 1044.4 }, { x: 1572.2, y: 1077.8 }, { x: 1572.2, y: 1133.3 }, { x: 1472.2, y: 1166.7 }] },
    // 114u 스프린트
    { kind: 'pipe', points: [{ x: 1566.7, y: 1105.6 }, { x: 1711.1, y: 1105.6 }], width: 63.3 },
  ],
  holes: [],
  gravity: { x: 0, y: 0 },
  damping: .32,
  rollingBelts: true,
  wallBounce: true,
  spawn: { kind: 'rect', x: 50, y: 90, w: 280, h: 356, pitch: 25, fill: 'spread' },
  startGate: { x: 333, y: 70, w: 13, h: 392 },
  finish: { kind: 'rect', x: 1690, y: 1074, w: 46, h: 64 },
  obstacles: [
    { kind: 'paddle', id: 'by-middle-sweep', x: 850, y: 266.7, length: 66, thickness: 8, speed: 1.9, phase: .65 },
    // 혼합 풀 — 회전 원반 ⌀400u · 0.4rad/s + 범퍼 ×4
    { kind: 'disk', id: 'by-disk', x: 1455.6, y: 388.9, r: 111.1, hubR: 16.7, speed: .4, grip: 1.0 },
    { kind: 'bumper', id: 'by-bump-1', x: 1400, y: 622.2, r: 21.1 },
    { kind: 'bumper', id: 'by-bump-2', x: 1511.1, y: 644.4, r: 21.1 },
    { kind: 'bumper', id: 'by-bump-3', x: 1444.4, y: 738.9, r: 21.1 },
    { kind: 'bumper', id: 'by-bump-4', x: 1533.3, y: 800, r: 21.1 },
    // 뱅크 2 파도 게이트 ×2 — 반쪽 바가 위상 엇갈려 여닫힌다 (주기 2.4s)
    { kind: 'gate', id: 'by-wave-b-top', x: 961.1, y: 727.8, w: 8, h: 34, schedule: { type: 'cycle', period: 2.4, openSeconds: 1.2, phase: 0 }, eventType: 'gate-frenzy' },
    { kind: 'gate', id: 'by-wave-b-bot', x: 961.1, y: 816.7, w: 8, h: 34, schedule: { type: 'cycle', period: 2.4, openSeconds: 1.2, phase: 1.2 }, eventType: 'gate-frenzy' },
    { kind: 'gate', id: 'by-wave-a-top', x: 1044.4, y: 727.8, w: 8, h: 34, schedule: { type: 'cycle', period: 2.4, openSeconds: 1.2, phase: 1.2 }, eventType: 'gate-frenzy' },
    { kind: 'gate', id: 'by-wave-a-bot', x: 1044.4, y: 816.7, w: 8, h: 34, schedule: { type: 'cycle', period: 2.4, openSeconds: 1.2, phase: 0 }, eventType: 'gate-frenzy' },
    // 벨트 C 페그 필드 (경사 9% 구간)
    { kind: 'pin', id: 'by-peg-1', x: 833.3, y: 1072.2, r: 14.4 },
    { kind: 'pin', id: 'by-peg-2', x: 900, y: 1105.6, r: 14.4 },
    { kind: 'pin', id: 'by-peg-3', x: 833.3, y: 1138.9, r: 14.4 },
    { kind: 'pin', id: 'by-peg-4', x: 966.7, y: 1072.2, r: 14.4 },
    { kind: 'pin', id: 'by-peg-5', x: 1033.3, y: 1105.6, r: 14.4 },
    { kind: 'pin', id: 'by-peg-6', x: 966.7, y: 1138.9, r: 14.4 },
  ],
  zones: [
    // 안정 / 스위퍼 / 고속·주기 역류: 각 레인에 실제 체류와 시간상 장단점이 있다.
    { kind: 'belt', rect: { x: 290, y: 76, w: 1055, h: 114 }, vx: 180 * V3, grip: 2.9, lateralGrip: 2.4, lane: 0 },
    { kind: 'belt', rect: { x: 290, y: 209, w: 1055, h: 115 }, vx: 260 * V3, grip: 2.9, lateralGrip: 2.4, lane: 1 },
    { kind: 'belt', rect: { x: 290, y: 342, w: 1055, h: 115 }, vx: 340 * V3, grip: 2.9, lateralGrip: 2.4, lane: 2 },
    // 역주행 패치 −180u/s (빠른 레인 240u) — 이벤트 시 700u·3레인 확장
    { kind: 'belt', rect: { x: 1140, y: 342, w: 133.3, h: 115 }, vx: -180 * V3, grip: 4.4, lateralGrip: 2.4, lane: 2, priority: 3, reversible: false, cycle: { period: 5, onSeconds: 1.6, phase: 0 }, eventType: 'patch-expand', eventRect: { x: 956, y: 342, w: 388.9, h: 115 }, tint: '#ff5ca8' },
    { kind: 'belt', rect: { x: 956, y: 76, w: 388.9, h: 114 }, vx: -180 * V3, grip: 4.4, lane: 0, priority: 3, eventOnly: true, eventType: 'patch-expand', tint: '#ff5ca8' },
    { kind: 'belt', rect: { x: 956, y: 209, w: 388.9, h: 115 }, vx: -180 * V3, grip: 4.4, lane: 1, priority: 3, eventOnly: true, eventType: 'patch-expand', tint: '#ff5ca8' },
    // 뱅크 2 — 역방향 3속 (풀 안까지 뻗어 바닥 마블을 건져 낸다)
    { kind: 'belt', rect: { x: 400, y: 597, w: 995, h: 117 }, vx: -180 * V3, grip: 2.9, lateralGrip: 2.4, lane: 0 },
    { kind: 'belt', rect: { x: 400, y: 730, w: 1020, h: 118 }, vx: -260 * V3, grip: 2.9, lateralGrip: 2.4, lane: 1 },
    { kind: 'belt', rect: { x: 330, y: 863, w: 1150, h: 118 }, vx: -340 * V3, grip: 2.9, lateralGrip: 2.4, lane: 2 },
    { kind: 'belt', rect: { x: 677.8, y: 863, w: 133.3, h: 118 }, vx: 180 * V3, grip: 4.4, lateralGrip: 2.4, lane: 2, priority: 3, reversible: false, cycle: { period: 5, onSeconds: 1.6, phase: 2.3 }, eventType: 'patch-expand', eventRect: { x: 550, y: 863, w: 388.9, h: 118 }, tint: '#ff5ca8' },
    { kind: 'belt', rect: { x: 550, y: 597, w: 388.9, h: 117 }, vx: 180 * V3, grip: 4.4, lane: 0, priority: 3, eventOnly: true, eventType: 'patch-expand', tint: '#ff5ca8' },
    { kind: 'belt', rect: { x: 550, y: 730, w: 388.9, h: 118 }, vx: 180 * V3, grip: 4.4, lane: 1, priority: 3, eventOnly: true, eventType: 'patch-expand', tint: '#ff5ca8' },
    // 벨트 C — 220u/s 광폭
    { kind: 'belt', rect: { x: 300, y: 1023, w: 940, h: 165 }, vx: 220 * V3, grip: 2.9, lane: 1 },
    // 역류 펌프 — 0.6초 간격 좌우 교대 측면 분사 (깔때기 −400u 지점)
    { kind: 'pump', rect: { x: 1150, y: 1024, w: 95, h: 163 }, ax: 0, ay: 112, period: 1.2 },
    // 경사 등가 가속 — 출발장 배출·풀 4%·야드·연결 곡선·페그 9%·챔버·깔때기
    { kind: 'accel', rect: { x: 40, y: 78, w: 298, h: 378 }, ax: 42, ay: 0 },
    { kind: 'accel', rect: { x: 1289, y: 80, w: 334, h: 795 }, ax: 0, ay: 34 },
    // 우측 상부 벽 압착 방지 — 안쪽·아래로 쓸어내리고 러시 부스트를 끈다
    { kind: 'accel', rect: { x: 1500, y: 180, w: 125, h: 460 }, ax: -30, ay: 20 },
    { kind: 'accel', rect: { x: 1460, y: 640, w: 165, h: 290 }, ax: -38, ay: 14 },
    { kind: 'accel', rect: { x: 1289, y: 855, w: 340, h: 110 }, ax: -36, ay: 0 },
    { kind: 'accel', rect: { x: 1289, y: 930, w: 120, h: 55 }, ax: -40, ay: 0 },
    { kind: 'accel', rect: { x: 85, y: 610, w: 305, h: 190 }, ax: -16, ay: 34 },
    { kind: 'accel', rect: { x: 85, y: 800, w: 245, h: 100 }, ax: 0, ay: 30 },
    // 야드 하부 → 연결 곡선 입구로 모아 내리는 깔때기
    { kind: 'accel', rect: { x: 150, y: 900, w: 175, h: 90 }, ax: -22, ay: 44 },
    { kind: 'accel', rect: { x: 172, y: 955, w: 135, h: 165 }, ax: 22, ay: 46 },
    { kind: 'accel', rect: { x: 805, y: 1023, w: 260, h: 165 }, ax: 36, ay: 0 },
    { kind: 'accel', rect: { x: 1240, y: 1030, w: 235, h: 152 }, ax: 34, ay: 0 },
    { kind: 'accel', rect: { x: 1472, y: 1040, w: 270, h: 130 }, ax: 66, ay: 0 },
  ],
  events: [
    { type: 'belt-reverse', duration: 4, window: [14, 22] },
    { type: 'patch-expand', duration: 5, window: [24, 32] },
    { type: 'gate-frenzy', duration: 4.5, window: [34, 42] },
  ],
  eventZones: [
    { type: 'belt-reverse', rect: { x: 290, y: 70, w: 1100, h: 392 } },
    { type: 'belt-reverse', rect: { x: 395, y: 592, w: 1090, h: 394 } },
    { type: 'patch-expand', rect: { x: 956, y: 70, w: 389, h: 392 } },
    { type: 'patch-expand', rect: { x: 550, y: 592, w: 389, h: 394 } },
    { type: 'gate-frenzy', rect: { x: 938, y: 715, w: 130, h: 145 } },
  ],
  landmarks: [
    { x: 838, y: 38, label: '안정 · 스위퍼 · 고속 역류' },
    { x: 1455.6, y: 46, label: '혼합 풀' },
    { x: 1207, y: 505, label: '주기 역류 · 꺼지면 고속 통과' },
    { x: 1003, y: 570, label: '파도 게이트' },
    { x: 233.3, y: 574, label: '전환 야드' },
    { x: 1197, y: 1005, label: '역류 펌프' },
  ],
  stallSeconds: 3.2,
  rushAt: 62,
  maxSpeed: 700 * V3,
  guideRecovery: true,
  velocityWatchdog: { minSpeed: 8 * V3, seconds: 1.2, impulse: 180 * V3 },
  eventMinGapSeconds: 8,
  eventFinalGuardSeconds: 5,
};

/** R2 가이드 경로 — 출발장 → 뱅크1 중앙 레인 → 혼합 풀 → 뱅크2 중앙 → 야드 → 벨트 C → 결승. */
export function beltYardGuidePath(): TrackPoint[] {
  return [
    { x: 120, y: 266.7, width: 340 },
    { x: 333.3, y: 266.7, width: 300 },
    { x: 700, y: 266.7, width: 111 },
    { x: 1150, y: 266.7, width: 111 },
    { x: 1330, y: 300, width: 160 },
    { x: 1430, y: 420, width: 240 },
    { x: 1455, y: 600, width: 300 },
    { x: 1455, y: 770, width: 300 },
    { x: 1430, y: 880, width: 220 },
    { x: 1300, y: 815, width: 150 },
    { x: 1180, y: 788.9, width: 111 },
    { x: 800, y: 788.9, width: 111 },
    { x: 480, y: 788.9, width: 111 },
    { x: 300, y: 788.9, width: 200 },
    { x: 233.3, y: 860, width: 240 },
    { x: 233.3, y: 966.7, width: 122 },
    { x: 253, y: 1044, width: 122 },
    { x: 316.7, y: 1105.6, width: 122 },
    { x: 560, y: 1105.6, width: 165 },
    { x: 900, y: 1105.6, width: 165 },
    { x: 1150, y: 1105.6, width: 165 },
    { x: 1355.6, y: 1105.6, width: 200 },
    { x: 1500, y: 1105.6, width: 110 },
    { x: 1620, y: 1105.6, width: 62 },
    { x: 1715, y: 1105.6, width: 56 },
  ];
}

// ---------------------------------------------------------------------------
// 맵 8 · 대소용돌이 (v3 리메이크 R1 — 소용돌이 계승, 1222×1289, 탑뷰)
// 이중 중심(605.6/522.2, 733.3) 반원을 잇는 나선 2바퀴. 폭 역전(안쪽이 더 넓음),
// 중앙 게이트 폐지 → ⌀520u 아레나 + 배수 홀 결승, 나선 컷 게이트 3개(한 바퀴 스킵).
// ---------------------------------------------------------------------------

const GV_CENTER_A = { x: 605.6, y: 733.3 } as const; // p1·p3 (하단 반원)
const GV_CENTER_B = { x: 522.2, y: 733.3 } as const; // p2·p4 (상단 반원)
// 도면(1015,1320)보다 51.7u 동쪽 — p4 상단 안쪽 벽과의 준접선(벽 2px)을 10px로 벌린다
const GV_ARENA = { x: 592.6, y: 733.3 } as const;

function grandArc(cx: number, cy: number, r: number, fromDeg: number, toDeg: number, steps: number): FieldPoint[] {
  const points: FieldPoint[] = [];
  for (let index = 0; index <= steps; index += 1) {
    const angle = (fromDeg + (toDeg - fromDeg) * index / steps) * DEG;
    points.push({ x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r });
  }
  return points;
}

export const GRAND_VORTEX_FIELD: FieldSpec = {
  eventScheduling: 'occupancy',
  shapes: [
    // 출발장 1420×300u (rx150)
    { kind: 'room', x: 111.1, y: 55.6, w: 788.9, h: 166.7, r: 83.3 },
    // 진입 슈트 — 외곽 접선 진입 (6%)
    {
      kind: 'pipe',
      points: [
        { x: 900, y: 138.9 }, { x: 1014.8, y: 156.9 }, { x: 1094, y: 207.6 },
        { x: 1140.2, y: 286.5 }, { x: 1155.6, y: 388.9 }, { x: 1140.3, y: 526.3 },
        { x: 1102.4, y: 644.4 }, { x: 1055.6, y: 733.3 },
      ],
      width: 105.6,
    },
    // 나선 — 폭 역전 180→200→215→230u
    { kind: 'pipe', points: grandArc(GV_CENTER_A.x, GV_CENTER_A.y, 450, 0, 180, 24), width: 100 },
    { kind: 'pipe', points: grandArc(GV_CENTER_B.x, GV_CENTER_B.y, 366.7, 180, 360, 22), width: 111.1 },
    { kind: 'pipe', points: grandArc(GV_CENTER_A.x, GV_CENTER_A.y, 283.3, 0, 180, 20), width: 119.4 },
    { kind: 'pipe', points: grandArc(GV_CENTER_B.x, GV_CENTER_B.y, 200, 180, 360, 16), width: 127.8 },
    // 중앙 아레나 ⌀520u
    { kind: 'disc', x: GV_ARENA.x, y: GV_ARENA.y, r: 144.4 },
    // 컷 게이트 통로 — 1(남, p1→p3) · 3(북, p2→p4) · 2(남동 45°, p1→p3)
    { kind: 'room', x: 555.6, y: 1052, w: 100, h: 100, r: 22 },
    { kind: 'room', x: 472.2, y: 398, w: 100, h: 95, r: 22 },
    { kind: 'poly', points: [{ x: 861.8, y: 1077.1 }, { x: 949.4, y: 989.5 }, { x: 871.6, y: 911.7 }, { x: 784, y: 999.3 }] },
  ],
  holes: [],
  gravity: { x: 0, y: 0 },
  damping: .55,
  wallBounce: true,
  spawn: { kind: 'rect', x: 125, y: 70, w: 760, h: 140, pitch: 25 },
  startGate: { x: 894, y: 58, w: 13, h: 166 },
  finish: { kind: 'circle', x: GV_ARENA.x, y: GV_ARENA.y, r: 33.3 },
  obstacles: [
    // 스핀 드럼 ⌀216u · 0.4rad/s — p2·p3 레인 위
    { kind: 'disk', id: 'gv-drum-1', x: 262.8, y: 473.9, r: 60, hubR: 10, speed: .4, grip: 1.9, eventType: 'disk-reverse' },
    { kind: 'disk', id: 'gv-drum-2', x: 405, y: 933.9, r: 60, hubR: 10, speed: .4, grip: 1.9, eventType: 'disk-reverse' },
    // 컷 게이트 1 (남) — 안·바깥 바가 2.6s 주기로 엇갈려 연다
    { kind: 'gate', id: 'gv-cut1-in', x: 555.6, y: 1072.2, w: 100, h: 7.8, schedule: { type: 'cycle', period: 2.6, openSeconds: 1.3, phase: 1.3 }, eventType: 'cut-open' },
    { kind: 'gate', id: 'gv-cut1-out', x: 555.6, y: 1121.7, w: 100, h: 7.8, schedule: { type: 'cycle', period: 2.6, openSeconds: 1.3, phase: 0 }, eventType: 'cut-open' },
    // 컷 게이트 2 (남동 45°) — 통로를 반씩 갈라 교대로 막는다
    { kind: 'gate', id: 'gv-cut2-a', x: 821.1, y: 990.5, w: 91.1, h: 7.8, angle: 45 * DEG, schedule: { type: 'cycle', period: 2.6, openSeconds: 1.3, phase: 0 }, eventType: 'cut-open' },
    { kind: 'gate', id: 'gv-cut2-b', x: 786.1, y: 1025.5, w: 91.1, h: 7.8, angle: 45 * DEG, schedule: { type: 'cycle', period: 2.6, openSeconds: 1.3, phase: 1.3 }, eventType: 'cut-open' },
    // 컷 게이트 3 (북)
    { kind: 'gate', id: 'gv-cut3-a', x: 472.2, y: 414.4, w: 100, h: 7.8, schedule: { type: 'cycle', period: 2.6, openSeconds: 1.3, phase: 1.3 }, eventType: 'cut-open' },
    { kind: 'gate', id: 'gv-cut3-b', x: 472.2, y: 461.7, w: 100, h: 7.8, schedule: { type: 'cycle', period: 2.6, openSeconds: 1.3, phase: 0 }, eventType: 'cut-open' },
    // 파도 게이트 ×2 (p2 북동 45° — 흐름을 좌우로 가르는 핀 4개)
    { kind: 'gate', id: 'gv-wave-1', x: 721.8, y: 472.7, w: 38.9, h: 7.8, angle: 45 * DEG, schedule: { type: 'cycle', period: 2.4, openSeconds: 1.2, phase: 1.2 } },
    { kind: 'gate', id: 'gv-wave-2', x: 759.5, y: 510.5, w: 38.9, h: 7.8, angle: 45 * DEG, schedule: { type: 'cycle', period: 2.4, openSeconds: 1.2, phase: 0 } },
    { kind: 'gate', id: 'gv-wave-3', x: 765, y: 429.5, w: 38.9, h: 7.8, angle: 45 * DEG, schedule: { type: 'cycle', period: 2.4, openSeconds: 1.2, phase: 0 } },
    { kind: 'gate', id: 'gv-wave-4', x: 802.7, y: 467.2, w: 38.9, h: 7.8, angle: 45 * DEG, schedule: { type: 'cycle', period: 2.4, openSeconds: 1.2, phase: 1.2 } },
    // 아레나 범퍼 ×6 (육각 배치)
    { kind: 'bumper', id: 'gv-bump-1', x: 675.9, y: 733.3, r: 18.9 },
    { kind: 'bumper', id: 'gv-bump-2', x: 634.3, y: 805.6, r: 18.9 },
    { kind: 'bumper', id: 'gv-bump-3', x: 550.9, y: 805.6, r: 18.9 },
    { kind: 'bumper', id: 'gv-bump-4', x: 509.3, y: 733.3, r: 18.9 },
    { kind: 'bumper', id: 'gv-bump-5', x: 550.9, y: 661.1, r: 18.9 },
    { kind: 'bumper', id: 'gv-bump-6', x: 634.3, y: 661.1, r: 18.9 },
  ],
  zones: [
    // 출발장 배출 + 슈트 6% (가이드 접선 가속)
    { kind: 'accel', rect: { x: 115, y: 58, w: 790, h: 164 }, ax: 46, ay: 0 },
    { kind: 'guide', rect: { x: 880, y: 60, w: 342, h: 640 }, accel: 58 },
    // 나선 — 내향 5% + 접선 구동, 부스터 링 이벤트 시 접선 가속 증폭
    { kind: 'radial', x: GV_CENTER_A.x, y: GV_CENTER_A.y, rMin: 392, rMax: 508, inward: 30, spin: 60, halfPlane: 'lower', eventType: 'boost-ring', eventSpinScale: 2.2 },
    { kind: 'radial', x: GV_CENTER_B.x, y: GV_CENTER_B.y, rMin: 305, rMax: 428, inward: 30, spin: 60, halfPlane: 'upper', eventType: 'boost-ring', eventSpinScale: 2.2 },
    { kind: 'radial', x: GV_CENTER_A.x, y: GV_CENTER_A.y, rMin: 218, rMax: 348, inward: 28, spin: 56, halfPlane: 'lower', eventType: 'boost-ring', eventSpinScale: 2.2 },
    { kind: 'radial', x: GV_CENTER_B.x, y: GV_CENTER_B.y, rMin: 132, rMax: 266, inward: 28, spin: 56, halfPlane: 'upper', eventType: 'boost-ring', eventSpinScale: 2.2 },
    // 아레나 6% 내향 — 배수 홀로 빨려 든다
    { kind: 'radial', x: GV_ARENA.x, y: GV_ARENA.y, rMin: 28, rMax: 168, inward: 58, spin: 26 },
    // 배수 홀 직전의 안정 공전만 감쇠한다. 바깥 범퍼 경합/중력은 유지하고
    // 대기 구간으로 분류하지 않아 정체 구제나 순간이동으로 순서를 바꾸지 않는다.
    { kind: 'wait', circle: { x: GV_ARENA.x, y: GV_ARENA.y, r: 60 }, calm: .3, waitForRelease: false },
    // 부스터 ×2 (p1 진입 직후 · p2 서측)
    { kind: 'accel', rect: { x: 990, y: 855, w: 90, h: 100 }, ax: -56, ay: 140 },
    { kind: 'accel', rect: { x: 132, y: 562, w: 90, h: 95 }, ax: 52, ay: -142 },
    // 역류 펌프 ×2 — 아레나 좌·우 측면 분사 0.6s 교대
    { kind: 'pump', rect: { x: 450.9, y: 655.6, w: 66.7, h: 155.5 }, ax: 112, ay: 0, period: 1.2 },
    { kind: 'pump', rect: { x: 667.6, y: 655.6, w: 66.7, h: 155.5 }, ax: 112, ay: 0, period: 1.2 },
  ],
  events: [
    { type: 'disk-reverse', duration: 5, window: [12, 20] },
    { type: 'cut-open', duration: 4, window: [20, 30] },
    { type: 'boost-ring', duration: 3.5, window: [34, 44] },
  ],
  eventZones: [
    { type: 'disk-reverse', circle: { x: 262.8, y: 473.9, r: 70 } },
    { type: 'disk-reverse', circle: { x: 405, y: 933.9, r: 70 } },
    { type: 'cut-open', rect: { x: 545, y: 1045, w: 120, h: 115 } },
    { type: 'cut-open', rect: { x: 462, y: 390, w: 120, h: 110 } },
    { type: 'cut-open', rect: { x: 775, y: 903, w: 185, h: 185 } },
    { type: 'boost-ring', circle: { x: GV_CENTER_A.x, y: GV_CENTER_A.y, r: 495 } },
  ],
  landmarks: [
    { x: 700, y: 1176, label: '컷 게이트 1' },
    { x: 1000, y: 1062, label: '컷 게이트 2' },
    { x: 600, y: 368, label: '컷 게이트 3' },
    { x: 168, y: 388, label: '스핀 드럼' },
    { x: 728, y: 592, label: '배수 홀 결승' },
  ],
  stallSeconds: 3.2,
  rushAt: 60,
  maxSpeed: 700 * V3,
  velocityWatchdog: { minSpeed: 8 * V3, seconds: 1.2, impulse: 180 * V3 },
  guideRecovery: true,
  guideRecoveryDistance: 130,
  eventMinGapSeconds: 8,
  eventFinalGuardSeconds: 5,
};

/** R1 가이드 경로 — 출발장 → 슈트 → 나선 2바퀴(반원 4개) → 아레나 → 배수 홀. */
export function grandVortexGuidePath(): TrackPoint[] {
  const points: TrackPoint[] = [
    { x: 170, y: 138.9, width: 300 },
    { x: 600, y: 138.9, width: 150 },
    { x: 890, y: 138.9, width: 105 },
    { x: 1014.8, y: 156.9, width: 105 },
    { x: 1094, y: 207.6, width: 105 },
    { x: 1140.2, y: 286.5, width: 105 },
    { x: 1155.6, y: 388.9, width: 105 },
    { x: 1140.3, y: 526.3, width: 105 },
    { x: 1102.4, y: 644.4, width: 105 },
  ];
  const spiral: Array<[number, number, number, number, number, number]> = [
    [GV_CENTER_A.x, GV_CENTER_A.y, 450, 0, 180, 100],
    [GV_CENTER_B.x, GV_CENTER_B.y, 366.7, 180, 360, 111],
    [GV_CENTER_A.x, GV_CENTER_A.y, 283.3, 0, 180, 119],
    [GV_CENTER_B.x, GV_CENTER_B.y, 200, 180, 360, 128],
  ];
  spiral.forEach(([cx, cy, r, from, to, width], arcIndex) => {
    // 반원 이음점(끝점=다음 시작점)은 중복 표본을 만들지 않게 건너뛴다
    const arc = grandArc(cx, cy, r, from, to, 10);
    for (const point of arcIndex === 0 ? arc : arc.slice(1)) points.push({ ...point, width });
  });
  points.push(
    { x: 716, y: 796, width: 120 },
    { x: 640, y: 818, width: 110 },
    { x: GV_ARENA.x, y: GV_ARENA.y, width: 66 },
  );
  return points;
}

// ---------------------------------------------------------------------------
// 맵 9 · 협곡 종주 (v3 롱맵 L1 — 1556×1111, 탑뷰 서펜타인)
// 4직선 + 뱅크턴 3개. 직선1 부스터·회전봉, 충돌 사발, 파도 게이트, 오버슛 램프,
// 분기 도박 3레인(A 좁고 짧음/C 중간/B 넓고 안전), 페그 필드 12 → 깔때기 결승.
// ---------------------------------------------------------------------------

// 형상과 구동 방향을 같은 경로에서 만든다. 중간 단면에는 세 통로 사이에 실제 벽이 있다.
export const CANYON_BRANCHES = [
  { id: 'A', width: 72, speed: 175, points: [{ x: 738.9, y: 711.1 }, { x: 800, y: 641 }, { x: 850, y: 622 }, { x: 970, y: 622 }, { x: 1030, y: 658 }, { x: 1097.2, y: 711.1 }] },
  { id: 'C', width: 72, speed: 140, points: [{ x: 738.9, y: 711.1 }, { x: 1097.2, y: 711.1 }] },
  { id: 'B', width: 84, speed: 160, points: [{ x: 738.9, y: 711.1 }, { x: 800, y: 781 }, { x: 850, y: 801 }, { x: 970, y: 801 }, { x: 1040, y: 763 }, { x: 1097.2, y: 711.1 }] },
];

function canyonBranchBelts(): FieldZone[] {
  return CANYON_BRANCHES.flatMap((branch) => branch.points.slice(1).map((end, index): FieldZone => {
    const original = branch.points[index];
    // 공통 분기점에서는 직선 관성으로 갈래를 고른다. 첫 A/B 벨트가 모든 공을 끌어가지 않는다.
    const start = index === 0 && branch.id !== 'C' ? { x: original.x + (end.x - original.x) * .5, y: original.y + (end.y - original.y) * .5 } : original;
    const dx = end.x - start.x, dy = end.y - start.y, length = Math.hypot(dx, dy);
    return { kind: 'belt', rect: { x: start.x - branch.width, y: Math.min(start.y, end.y) - branch.width, w: dx + branch.width * 2, h: Math.abs(dy) + branch.width * 2 },
      capsule: { points: [start, end], width: branch.width }, vx: dx / length * branch.speed, vy: dy / length * branch.speed,
      grip: 2.2, lane: 0, priority: branch.id === 'C' ? 2 : 1, chevrons: false };
  }));
}

export const CANYON_FIELD: FieldSpec = {
  eventScheduling: 'occupancy',
  shapes: [
    // 출발장 580×420u 라운드 스타디움 + 진입 깔때기 250→150u
    { kind: 'room', x: 44.4, y: 72.2, w: 322.2, h: 233.3, r: 83.3 },
    { kind: 'poly', points: [{ x: 355.6, y: 105.6 }, { x: 466.7, y: 148.9 }, { x: 466.7, y: 228.9 }, { x: 355.6, y: 272.2 }] },
    // 직선 1 (부스터 ×3 · 회전봉 ×2)
    { kind: 'pipe', points: [{ x: 461.1, y: 188.9 }, { x: 1290, y: 192 }], width: 92 },
    // 뱅크턴 1 (R390 · 폭 180u)
    {
      kind: 'pipe',
      points: [
        { x: 1222.2, y: 188.9 },
        { x: 1290.8, y: 193.3 },
        { x: 1346.5, y: 205.8 },
        { x: 1389.6, y: 225.0 },
        { x: 1420.2, y: 249.8 },
        { x: 1438.4, y: 278.9 },
        { x: 1444.4, y: 311.1 },
        { x: 1438.4, y: 345.2 },
        { x: 1420.2, y: 374.9 },
        { x: 1389.6, y: 399.3 },
        { x: 1346.5, y: 417.7 },
        { x: 1290.8, y: 429.3 },
        { x: 1222.2, y: 433.3 },
      ],
      width: 120,
    },
    // 직선 2 → 충돌 사발 ⌀430u → 파도 게이트 통로
    { kind: 'pipe', points: [{ x: 1290, y: 430 }, { x: 860, y: 436 }], width: 92 },
    { kind: 'disc', x: 788.9, y: 444.4, r: 119.4 },
    { kind: 'pipe', points: [{ x: 730, y: 456 }, { x: 445, y: 464 }], width: 92 },
    // 뱅크턴 2 (오버슛 램프 내장)
    {
      kind: 'pipe',
      points: [
        { x: 500.0, y: 461.1 },
        { x: 436.1, y: 465.6 },
        { x: 383.5, y: 478.2 },
        { x: 342.4, y: 497.9 },
        { x: 312.8, y: 523.7 },
        { x: 294.9, y: 554.3 },
        { x: 288.9, y: 588.9 },
        { x: 294.9, y: 623.0 },
        { x: 312.8, y: 652.7 },
        { x: 342.4, y: 677.1 },
        { x: 383.5, y: 695.5 },
        { x: 436.1, y: 707.1 },
        { x: 500.0, y: 711.1 },
      ],
      width: 120,
    },
    // 직선 3 → 분리된 세 갈래: A 빠른 페그, C 직통 주기 문, B 넓은 우회
    { kind: 'pipe', points: [{ x: 440, y: 708 }, { x: 760, y: 711.1 }], width: 92 },
    { kind: 'disc', x: 710, y: 711, r: 102 },
    ...CANYON_BRANCHES.map((branch) => ({ kind: 'pipe' as const, points: branch.points, width: branch.width })),
    // 합류 광폭 → 뱅크턴 3
    { kind: 'pipe', points: [{ x: 1075, y: 711.1 }, { x: 1320, y: 714 }], width: 111.1 },
    {
      kind: 'pipe',
      points: [
        { x: 1261.1, y: 711.1 },
        { x: 1325.4, y: 715.5 },
        { x: 1379.0, y: 728.0 },
        { x: 1421.5, y: 747.2 },
        { x: 1452.5, y: 772.0 },
        { x: 1471.4, y: 801.1 },
        { x: 1477.8, y: 833.3 },
        { x: 1471.4, y: 867.5 },
        { x: 1452.5, y: 897.1 },
        { x: 1421.5, y: 921.5 },
        { x: 1379.0, y: 939.9 },
        { x: 1325.4, y: 951.5 },
        { x: 1261.1, y: 955.6 },
      ],
      width: 120,
    },
    // 직선 4 (페그 필드 · 역류 펌프) → 결승 깔때기 190→78u → 330u 스프린트
    { kind: 'pipe', points: [{ x: 1320, y: 952 }, { x: 430, y: 955.6 }], width: 105.6 },
    { kind: 'poly', points: [{ x: 461.1, y: 902.8 }, { x: 316.7, y: 951.1 }, { x: 316.7, y: 993.3 }, { x: 461.1, y: 1008.3 }] },
    { kind: 'pipe', points: [{ x: 320, y: 972.2 }, { x: 138.9, y: 972.2 }], width: 43.3 },
  ],
  holes: [],
  gravity: { x: 0, y: 0 },
  damping: .32,
  rollingBelts: true,
  wallBounce: true,
  spawn: { kind: 'rect', x: 58, y: 86, w: 294, h: 206, pitch: 21 },
  startGate: { x: 462, y: 146, w: 12, h: 86 },
  finish: { kind: 'rect', x: 130, y: 951, w: 30, h: 42 },
  obstacles: [
    // 직선 1 회전봉 ±110°/s
    { kind: 'paddle', id: 'cy-spin-1', x: 655.6, y: 215.6, length: 58, thickness: 6.1, speed: 110 * DEG, phase: 26 * DEG },
    { kind: 'paddle', id: 'cy-spin-2', x: 900, y: 164.4, length: 58, thickness: 6.1, speed: -110 * DEG, phase: -20 * DEG },
    // 충돌 사발 범퍼 ×5
    { kind: 'bumper', id: 'cy-bowl-1', x: 751.1, y: 395.6, r: 14.4, restitution: .45 },
    { kind: 'bumper', id: 'cy-bowl-2', x: 837.8, y: 418.9, r: 14.4, restitution: .45 },
    { kind: 'bumper', id: 'cy-bowl-3', x: 740, y: 491.1, r: 14.4, restitution: .45 },
    { kind: 'bumper', id: 'cy-bowl-4', x: 827.8, y: 500, r: 14.4, restitution: .45 },
    { kind: 'bumper', id: 'cy-bowl-5', x: 791.1, y: 444.4, r: 14.4, restitution: .45 },
    // 파도 게이트 ×2 (2.4s · 위/아래 반쪽 바) — 통로 벽에 스냅
    { kind: 'gate', id: 'cy-wave-a-top', x: 597, y: 419.4, w: 6, h: 32, schedule: { type: 'cycle', period: 2.4, openSeconds: 1.2, phase: 1.2 }, eventType: 'gate-frenzy' },
    { kind: 'gate', id: 'cy-wave-a-bot', x: 597, y: 473.3, w: 6, h: 30, schedule: { type: 'cycle', period: 2.4, openSeconds: 1.2, phase: 0 }, eventType: 'gate-frenzy' },
    { kind: 'gate', id: 'cy-wave-b-top', x: 547, y: 419.4, w: 6, h: 32, schedule: { type: 'cycle', period: 2.4, openSeconds: 1.2, phase: 0 }, eventType: 'gate-frenzy' },
    { kind: 'gate', id: 'cy-wave-b-bot', x: 547, y: 473.3, w: 6, h: 30, schedule: { type: 'cycle', period: 2.4, openSeconds: 1.2, phase: 1.2 }, eventType: 'gate-frenzy' },
    // 상단 벽을 타던 공만 먼저 섞고, 중앙·하단에는 직통 진입 여유를 남긴다.
    // 큰 중앙 회전봉은 C로 진입하는 공까지 양쪽 우회 레인으로 쳐내던 구조였다.
    { kind: 'paddle', id: 'cy-fork-mixer', x: 710, y: 671, length: 94, thickness: 7, speed: 1.9, phase: .5, reverseEvery: 3.4 },
    // 분기 A레인 — 폐쇄 이벤트 게이트(평소 개방) + 리스크 페그 ×3
    { kind: 'gate', id: 'cy-branch-a', x: 795, y: 603, w: 8, h: 76, angle: -20 * DEG, schedule: { type: 'event-close' }, eventType: 'drop-close' },
    { kind: 'pin', id: 'cy-apeg-1', x: 866, y: 622, r: 8 },
    { kind: 'pin', id: 'cy-apeg-2', x: 928, y: 622, r: 8 },
    { kind: 'pin', id: 'cy-apeg-3', x: 996, y: 638, r: 8 },
    { kind: 'gate', id: 'cy-branch-c-cycle', x: 904, y: 671, w: 8, h: 80, schedule: { type: 'cycle', period: 4, openSeconds: 3.1, phase: .6 } },
    // 페그 필드 12 — 최종 셔플
    // 상·하열은 도면(y926.7/984.4)보다 중앙으로 당겨 벽-페그 틈을 26px로 확보
    // (도면 간격 14.5px는 마블 ⌀20이 못 지나 벽을 타던 마블이 쐐기로 낀다)
    { kind: 'pin', id: 'cy-peg-1', x: 1090, y: 938, r: 9 },
    { kind: 'pin', id: 'cy-peg-2', x: 1044.4, y: 955.6, r: 9 },
    { kind: 'pin', id: 'cy-peg-3', x: 1110, y: 973, r: 9 },
    { kind: 'pin', id: 'cy-peg-4', x: 978.9, y: 938, r: 9 },
    { kind: 'pin', id: 'cy-peg-5', x: 933.3, y: 955.6, r: 9 },
    { kind: 'pin', id: 'cy-peg-6', x: 998.9, y: 973, r: 9 },
    { kind: 'pin', id: 'cy-peg-7', x: 867.8, y: 938, r: 9 },
    { kind: 'pin', id: 'cy-peg-8', x: 822.2, y: 955.6, r: 9 },
    { kind: 'pin', id: 'cy-peg-9', x: 887.8, y: 973, r: 9 },
    { kind: 'pin', id: 'cy-peg-10', x: 756.7, y: 938, r: 9 },
    { kind: 'pin', id: 'cy-peg-11', x: 711.1, y: 955.6, r: 9 },
    { kind: 'pin', id: 'cy-peg-12', x: 776.7, y: 973, r: 9 },
    // 후반 위·아래 벽의 연속 우회선을 엇박 스위퍼로 끊는다. 반대편은
    // 항상 마블이 지나갈 틈을 남겨 통로 전체를 막는 문이 되지 않는다.
    { kind: 'paddle', id: 'cy-final-sweep-bottom', x: 1200, y: 970, length: 66, thickness: 6, speed: -1.8, phase: -.6, reverseEvery: 4.1 },
    { kind: 'paddle', id: 'cy-final-sweep-top', x: 635, y: 938, length: 64, thickness: 6, speed: 2.1, phase: .8, reverseEvery: 2.9 },
  ],
  zones: [
    // 출발장 배출. 직선은 rollingBelts의 전방 구동력으로 굴리고 충돌 관성을 보존한다.
    { kind: 'accel', rect: { x: 48, y: 76, w: 415, h: 226 }, ax: 40, ay: 0 },
    { kind: 'belt', rect: { x: 462, y: 145, w: 770, h: 90 }, vx: 150, grip: 2.2, lane: 0, chevrons: false },
    { kind: 'accel', rect: { x: 515.6, y: 147, w: 80, h: 84 }, ax: 170, ay: 0, eventType: 'boost-surge', eventScale: 1.8 },
    { kind: 'accel', rect: { x: 726.7, y: 147, w: 80, h: 84 }, ax: 170, ay: 0, eventType: 'boost-surge', eventScale: 1.8 },
    { kind: 'accel', rect: { x: 937.8, y: 147, w: 80, h: 84 }, ax: 170, ay: 0, eventType: 'boost-surge', eventScale: 1.8 },
    // 뱅크 입구 — 직선 벨트에서 곡선 순환으로의 인계 구간 방향 정합
    { kind: 'belt', rect: { x: 1222, y: 139, w: 250, h: 120 }, capsule: { points: [{ x: 1222.2, y: 188.9 }, { x: 1389.6, y: 225 }], width: 120 }, vx: 146, vy: 31, grip: 2.2, lane: 0, priority: 1, chevrons: false },
    { kind: 'belt', rect: { x: 300, y: 430, w: 220, h: 120 }, capsule: { points: [{ x: 500, y: 461.1 }, { x: 342.4, y: 497.9 }], width: 120 }, vx: -136, vy: 32, grip: 2.2, lane: 0, priority: 1, chevrons: false },
    { kind: 'belt', rect: { x: 1261, y: 660, w: 220, h: 120 }, capsule: { points: [{ x: 1261.1, y: 711.1 }, { x: 1421.5, y: 747.2 }], width: 120 }, vx: 146, vy: 33, grip: 2.2, lane: 0, priority: 1, chevrons: false },
    // 뱅크턴 — 내향+접선 순환 (외벽 포켓에 팩이 아치로 끼는 것을 내향이 계속 푼다)
    { kind: 'radial', x: 1227.8, y: 311.1, rMin: 60, rMax: 288, inward: 25, spin: 55 },
    { kind: 'radial', x: 500, y: 586.1, rMin: 55, rMax: 265, inward: 24, spin: -55 },
    { kind: 'radial', x: 1261.1, y: 833.3, rMin: 60, rMax: 288, inward: 25, spin: 55 },
    // 뱅크 정점 외벽 드레인 보조 — 정점 벽에 눌린 마블을 굽이 방향으로 흘려보낸다
    { kind: 'accel', rect: { x: 1448, y: 255, w: 62, h: 150 }, ax: -30, ay: 60 },
    { kind: 'accel', rect: { x: 222, y: 530, w: 62, h: 165 }, ax: 35, ay: 60 },
    { kind: 'accel', rect: { x: 1436, y: 795, w: 108, h: 168 }, ax: -35, ay: 60 },
    { kind: 'accel', rect: { x: 662, y: 330, w: 82, h: 92 }, ax: -10, ay: 70 },
    // 직선 2 → 사발 드레인(출구 좌측 1개, 문 방향 조준) → 파도 게이트 통로
    { kind: 'belt', rect: { x: 880, y: 390, w: 410, h: 88 }, vx: -140, grip: 2.2, lane: 0, chevrons: false },
    { kind: 'accel', rect: { x: 670, y: 330, w: 240, h: 105 }, ax: -45, ay: 32 },
    { kind: 'accel', rect: { x: 670, y: 435, w: 240, h: 130 }, ax: -45, ay: -16 },
    { kind: 'accel', rect: { x: 655, y: 420, w: 85, h: 82 }, ax: -52, ay: 0 },
    { kind: 'belt', rect: { x: 445, y: 418, w: 225, h: 88 }, vx: -130, grip: 2.2, lane: 0, chevrons: false },
    // 직선 3 → 분기(→5%) → 합류
    { kind: 'belt', rect: { x: 440, y: 665, w: 355, h: 92 }, vx: 140, grip: 2.2, lane: 0, chevrons: false },
    ...canyonBranchBelts(),
    { kind: 'belt', rect: { x: 1075, y: 655, w: 250, h: 115 }, vx: 150, grip: 2.2, lane: 0, chevrons: false },
    // 직선 4 (페그 9%) → 깔때기 9% → 스프린트
    { kind: 'belt', rect: { x: 430, y: 900, w: 890, h: 110 }, vx: -160, grip: 2.4, lane: 0, chevrons: false },
    { kind: 'belt', rect: { x: 128, y: 898, w: 305, h: 112 }, vx: -170, grip: 2.6, lane: 0, chevrons: false },
    // 역류 펌프 — 깔때기 −400u 지점, 0.6s 상하 교대
    { kind: 'pump', rect: { x: 552, y: 912, w: 56, h: 88 }, ax: 0, ay: 112, period: 1.2 },
  ],
  events: [
    { type: 'gate-frenzy', duration: 5, window: [12, 18] },
    { type: 'boost-surge', duration: 4, window: [22, 30] },
    { type: 'drop-close', duration: 6, window: [30, 38] },
  ],
  eventZones: [
    { type: 'gate-frenzy', rect: { x: 528, y: 410, w: 92, h: 102 } },
    { type: 'boost-surge', rect: { x: 500, y: 140, w: 560, h: 98 } },
    { type: 'drop-close', rect: { x: 766, y: 597, w: 250, h: 75 } },
  ],
  landmarks: [
    { x: 790, y: 118, label: '부스터 웨이브' },
    { x: 789, y: 316, label: '충돌 사발' },
    { x: 575, y: 380, label: '파도 게이트' },
    { x: 919, y: 560, label: 'A 급류 · 페그' },
    { x: 920, y: 678, label: 'C 직통 · 주기 문' },
    { x: 920, y: 865, label: 'B 넓은 우회' },
    { x: 905, y: 1042, label: '페그 · 엇박 스위퍼' },
    { x: 582, y: 1046, label: '역류 펌프' },
  ],
  stallSeconds: 3,
  rushAt: 68,
  maxSpeed: 700 * V3,
  velocityWatchdog: { minSpeed: 8 * V3, seconds: 1.2, impulse: 180 * V3 },
  crowdAgitation: true,
  eventMinGapSeconds: 8,
  eventFinalGuardSeconds: 5,
};

/** L1 가이드 경로 — 출발장 → 직선1 → 뱅크1 → 사발 → 뱅크2 → 분기(C 중앙) → 뱅크3 → 페그 → 결승. */
export function canyonGuidePath(): TrackPoint[] {
  return [
    { x: 110, y: 188.9, width: 300 },
    { x: 300, y: 188.9, width: 290 },
    { x: 420, y: 188.9, width: 170 },
    { x: 470, y: 188.9, width: 84 },
    { x: 700, y: 188.9, width: 84 },
    { x: 950, y: 188.9, width: 84 },
    { x: 1222.2, y: 188.9, width: 84 },
    { x: 1320.2, y: 198.6, width: 100 },
    { x: 1389.6, y: 225, width: 100 },
    { x: 1430.8, y: 263.9, width: 100 },
    { x: 1444.4, y: 311.1, width: 100 },
    { x: 1430.8, y: 360.7, width: 100 },
    { x: 1389.6, y: 399.3, width: 100 },
    { x: 1320.2, y: 424.4, width: 100 },
    { x: 1222.2, y: 433.3, width: 84 },
    { x: 1050, y: 433.3, width: 84 },
    { x: 922.2, y: 433.3, width: 84 },
    { x: 788.9, y: 444.4, width: 238 },
    { x: 675, y: 461.1, width: 84 },
    { x: 575, y: 461.1, width: 84 },
    { x: 408.4, y: 470.9, width: 100 },
    { x: 342.4, y: 497.9, width: 100 },
    { x: 302.3, y: 538.5, width: 100 },
    { x: 288.9, y: 588.9, width: 100 },
    { x: 302.3, y: 638.5, width: 100 },
    { x: 342.4, y: 677.1, width: 100 },
    { x: 408.4, y: 702.2, width: 100 },
    { x: 500, y: 711.1, width: 89 },
    { x: 620, y: 711.1, width: 89 },
    { x: 738.9, y: 711.1, width: 89 },
    { x: 900, y: 711.1, width: 58 },
    { x: 1050, y: 711.1, width: 58 },
    { x: 1097.2, y: 711.1, width: 111 },
    { x: 1180, y: 711.1, width: 111 },
    { x: 1353.6, y: 720.8, width: 100 },
    { x: 1421.5, y: 747.2, width: 100 },
    { x: 1463.5, y: 786.1, width: 100 },
    { x: 1477.8, y: 833.3, width: 100 },
    { x: 1463.5, y: 882.9, width: 100 },
    { x: 1421.5, y: 921.5, width: 100 },
    { x: 1353.6, y: 946.6, width: 100 },
    { x: 1261.1, y: 955.6, width: 106 },
    { x: 1000, y: 955.6, width: 106 },
    { x: 700, y: 955.6, width: 106 },
    { x: 500, y: 955.6, width: 106 },
    { x: 390, y: 968, width: 80 },
    { x: 300, y: 972.2, width: 43 },
    { x: 200, y: 972.2, width: 43 },
    { x: 145, y: 972.2, width: 43 },
  ];
}

export const FIELD_SPECS = {
  conveyor: CONVEYOR_FIELD,
  vortex: VORTEX_FIELD,
  seesaw: SEESAW_FIELD,
  'gravity-hub': GRAVITY_HUB_FIELD,
  pinball: PINBALL_FIELD,
  lift: LIFT_FIELD,
  'belt-yard': BELT_YARD_FIELD,
  'grand-vortex': GRAND_VORTEX_FIELD,
  canyon: CANYON_FIELD,
  tower: TOWER_FIELD,
  'gravity-bowls': GRAVITY_BOWLS_FIELD,
} as const;
